import React, { useState, useRef } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Icon, useToast, Image,
} from '@chakra-ui/react';
import { FiImage } from 'react-icons/fi';
import { camionesApi, UPLOADS_BASE, Camion } from '../../../api/serviciosApi';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

export const ESTADOS_CAMION = ['Disponible', 'En ruta', 'Recolectando', 'En traslado', 'Fuera de servicio'];

export const estadoCamionColor = (estado?: string) => {
  if (estado === 'Disponible') return 'green';
  if (estado === 'Fuera de servicio') return 'red';
  if (estado === 'En ruta' || estado === 'Recolectando') return 'blue';
  return 'orange';
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">
      {children}
    </Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const FormularioCamion: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [numeroEconomico, setNumeroEconomico] = useState(data?.numeroEconomico || '');
  const [placas, setPlacas] = useState(data?.placas || '');
  const [marca, setMarca] = useState(data?.marca || '');
  const [modelo, setModelo] = useState(data?.modelo || '');
  const [anio, setAnio] = useState(data?.anio != null ? String(data.anio) : '');
  const [capacidad, setCapacidad] = useState(data?.capacidad || '');
  const [descripcion, setDescripcion] = useState(data?.descripcion || '');
  const [estadoCamion, setEstadoCamion] = useState(data?.estadoCamion || 'Disponible');
  const [fotografia, setFotografia] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [guardando, setGuardando] = useState(false);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f && f.type.startsWith('image/')) {
      setFotografia(f);
      setPreview(URL.createObjectURL(f));
    } else if (f) {
      toast({ title: 'Solo se permiten imágenes', status: 'warning', duration: 3000 });
    }
    e.target.value = '';
  };

  const handleGuardar = async () => {
    if (!numeroEconomico) {
      toast({ title: 'Campo obligatorio', description: 'El número económico es requerido.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append('numeroEconomico', numeroEconomico);
      fd.append('placas', placas);
      fd.append('marca', marca);
      fd.append('modelo', modelo);
      if (anio) fd.append('anio', anio);
      fd.append('capacidad', capacidad);
      fd.append('descripcion', descripcion);
      fd.append('estadoCamion', estadoCamion);
      if (fotografia) fd.append('fotografia', fotografia);

      if (modo === 'edit') {
        await camionesApi.update(data.id, fd);
      } else {
        await camionesApi.create(fd);
      }
      toast({ title: modo === 'edit' ? 'Camión actualizado' : 'Camión registrado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view') {
    const d: Camion = data || {};
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos del Camión</SectionTitle>
        {d.fotografia && (
          <Image src={`${UPLOADS_BASE}${d.fotografia}`} alt={d.numeroEconomico} maxH="180px" objectFit="cover" borderRadius="md" />
        )}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="NÚMERO ECONÓMICO" value={d.numeroEconomico} />
          <Field label="ESTADO" value={<Badge colorScheme={estadoCamionColor(d.estadoCamion)}>{d.estadoCamion}</Badge>} />
          <Field label="PLACAS" value={d.placas} />
          <Field label="MARCA" value={d.marca} />
          <Field label="MODELO" value={d.modelo} />
          <Field label="AÑO" value={d.anio} />
          <Field label="CAPACIDAD" value={d.capacidad} />
        </SimpleGrid>
        {d.descripcion && (
          <>
            <SectionTitle>Descripción</SectionTitle>
            <Text fontSize="sm" whiteSpace="pre-wrap">{d.descripcion}</Text>
          </>
        )}
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos del Camión</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Número económico</FormLabel>
          <Input placeholder="Ej. RB-01" value={numeroEconomico} onChange={(e) => setNumeroEconomico(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estado</FormLabel>
          <Select value={estadoCamion} onChange={(e) => setEstadoCamion(e.target.value)}>
            {ESTADOS_CAMION.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Placas</FormLabel>
          <Input value={placas} onChange={(e) => setPlacas(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Marca</FormLabel>
          <Input value={marca} onChange={(e) => setMarca(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Modelo</FormLabel>
          <Input value={modelo} onChange={(e) => setModelo(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Año</FormLabel>
          <Input type="number" value={anio} onChange={(e) => setAnio(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Capacidad</FormLabel>
          <Input placeholder="Ej. 10 toneladas" value={capacidad} onChange={(e) => setCapacidad(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Descripción</SectionTitle>
      <Textarea rows={3} placeholder="Observaciones del vehículo..." value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />

      <SectionTitle>Fotografía</SectionTitle>
      {modo === 'edit' && data?.fotografia && !preview && (
        <Image src={`${UPLOADS_BASE}${data.fotografia}`} alt="actual" maxH="120px" objectFit="cover" borderRadius="md" />
      )}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      <Box
        border="2px dashed" borderColor="gray.300" borderRadius="md" p={4} textAlign="center"
        cursor="pointer" _hover={{ borderColor: '#5b182b', bg: 'red.50' }}
        onClick={() => fileInputRef.current?.click()}
      >
        {preview ? (
          <VStack>
            <Image src={preview} maxH="120px" objectFit="cover" borderRadius="md" />
            <Text fontSize="xs" color="gray.400">Haz clic para cambiar</Text>
          </VStack>
        ) : (
          <HStack justify="center" color="gray.500">
            <Icon as={FiImage} />
            <Text fontSize="sm">Haz clic para seleccionar una imagen</Text>
          </HStack>
        )}
      </Box>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioCamion;
